import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase'; 
import {Post} from './post'
import {useAuthState} from 'react-firebase-hooks/auth'
import {auth} from '../../config/firebase'
import './post.css'
import DogLoginImage from '../../images/dog.png'

export interface PostType {
    id: string;
    userId: string;
    title: string;
    username: string;
    description: string;
    hyperlink: string;
    pdflink: string;
    selectedCourseOption: string;
    selectedSchoolOption: string;
    selectedUnitOption: string;
}

interface UserRole {
    isAdmin: boolean;
    canManageCourses: boolean;
    canViewPosts: boolean;
    canCreatePosts: boolean;
    canManageUnits: boolean;
}

const DefaultScreen = () =>(
    <div className="defaultscreen">
        <div className="post-home-container">
        <img src={DogLoginImage} alt="original post" className="dog-image-size"/>
        <h1 className="mb-4 text-4xl font-inter leading-none tracking-tight text-gray-900 md:text-4xl lg:text-5xl dark:text-white"><span className="gradient-text-ace">Welcome to the Posts page!</span></h1>
                <div className="/underlined">
                <p className="mb-6 text-lg font-inter text-gray-300 lg:text-xl sm:px-16 xl:px-48 dark:text-gray-400">Select a school and a class to get started.</p>
                </div>
        </div>
    </div>
);

export const Main = () => {
    const [filteredPosts, setFilteredPosts] = useState<PostType[]>([]);
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedUnitFilter, setSelectedUnitFilter] = useState('');
    const [schoolOptions, setSchoolOptions] = useState<string[]>([]);
    const [courseOptions, setCourseOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<UserRole | null>(null);

    const [user] = useAuthState(auth);

    // Fetch user role and permissions
    useEffect(() => {
        const fetchUserRole = async () => {
            if (!user?.uid) {
                setUserRole(null);
                return;
            }
            
            try {
                const roleQuery = query(
                    collection(db, "userRoles"), 
                    where("userId", "==", user.uid)
                );
                const roleSnapshot = await getDocs(roleQuery);
                
                if (!roleSnapshot.empty) {
                    const role = roleSnapshot.docs[0].data() as UserRole;
                    setUserRole(role);
                } else {
                    setUserRole(null);
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
                setUserRole(null);
            }
        };

        fetchUserRole();
    }, [user]);

    // Permission check function (not needed in individual posts anymore)
    const canUserModifyPost = (post: PostType) => {
        if (userRole?.isAdmin) return true;
        if (userRole?.canManageCourses) return true;
        if (post.userId === user?.uid) return true;
        return false;
    };

    // Callback for when a post is deleted from Post component
    const handlePostDeleted = (postId: string) => {
        setFilteredPosts(prev => prev.filter(p => p.id !== postId));
    };

    // Fetch filtered posts based on selected filters
    const fetchFilteredPosts = async (school: string, course: string) => {
        setLoading(true);
        setError(null);
        
        try {
            let q;
            
            if (school && course) {
                q = query(
                    collection(db, "posts"),
                    where("selectedSchoolOption", "==", school),
                    where("selectedCourseOption", "==", course)
                );
            } else if (school) {
                q = query(
                    collection(db, "posts"),
                    where("selectedSchoolOption", "==", school)
                );
            } else if (course) {
                q = query(
                    collection(db, "posts"),
                    where("selectedCourseOption", "==", course)
                );
            } else {
                setFilteredPosts([]);
                setLoading(false);
                return;
            }
            
            const querySnapshot = await getDocs(q);
            const posts = querySnapshot.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id
            })) as PostType[];
            
            setFilteredPosts(posts);
        } catch (err) {
            console.error("Error fetching filtered posts:", err);
            setError("Failed to fetch posts. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch unique school options
    const fetchSchoolOptions = async () => {
        try {
            const postsSnapshot = await getDocs(collection(db, "posts"));
            const schools = new Set<string>();
            
            postsSnapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.selectedSchoolOption) {
                    schools.add(data.selectedSchoolOption);
                }
            });
            
            setSchoolOptions(Array.from(schools).sort());
        } catch (err) {
            console.error("Error fetching school options:", err);
        }
    };

    // Fetch course options based on selected school
    const fetchCourseOptions = async (school: string) => {
        try {
            if (!school) {
                setCourseOptions([]);
                return;
            }
            
            const q = query(
                collection(db, "posts"),
                where("selectedSchoolOption", "==", school)
            );
            
            const querySnapshot = await getDocs(q);
            const courses = new Set<string>();
            
            querySnapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.selectedCourseOption) {
                    courses.add(data.selectedCourseOption);
                }
            });
            
            setCourseOptions(Array.from(courses).sort());
        } catch (err) {
            console.error("Error fetching course options:", err);
        }
    };

    // Load school options on component mount
    useEffect(() => {
        fetchSchoolOptions();
    }, []);

    // Load course options when school changes
    useEffect(() => {
        fetchCourseOptions(selectedSchool);
        if (selectedCourse) {
            setSelectedCourse('');
        }
    }, [selectedSchool]);

    // Fetch posts when filters change
    useEffect(() => {
        if (selectedSchool || selectedCourse) {
            fetchFilteredPosts(selectedSchool, selectedCourse);
        } else {
            setFilteredPosts([]);
        }
    }, [selectedSchool, selectedCourse]);

    // Reset unit filter when school or course changes
    useEffect(() => {
        setSelectedUnitFilter('');
    }, [selectedSchool, selectedCourse]);

    // Group posts by unit for display, with optional unit filtering
    const groupedPosts = React.useMemo(() => {
        if (!selectedSchool || !selectedCourse) return {};
        
        // Filter posts by selected unit if one is chosen
        const postsToGroup = selectedUnitFilter 
            ? filteredPosts.filter(post => post.selectedUnitOption === selectedUnitFilter)
            : filteredPosts;
        
        const groups: { [unit: string]: PostType[] } = {};
        
        postsToGroup.forEach(post => {
            const unit = post.selectedUnitOption || "No Unit Specified";
            if (!groups[unit]) {
                groups[unit] = [];
            }
            groups[unit].push(post);
        });
        
        // Sort units alphabetically, but put "No Unit Specified" last
        const sortedGroups: { [unit: string]: PostType[] } = {};
        const sortedUnits = Object.keys(groups).sort((a, b) => {
            if (a === "No Unit Specified") return 1;
            if (b === "No Unit Specified") return -1;
            return a.localeCompare(b);
        });
        
        sortedUnits.forEach(unit => {
            sortedGroups[unit] = groups[unit];
        });
        
        return sortedGroups;
    }, [filteredPosts, selectedSchool, selectedCourse, selectedUnitFilter]);

    // Get unique units from current posts for the dropdown
    const availableUnits = React.useMemo(() => {
        const units = new Set<string>();
        filteredPosts.forEach(post => {
            const unit = post.selectedUnitOption || "No Unit Specified";
            units.add(unit);
        });
        
        return Array.from(units).sort((a, b) => {
            if (a === "No Unit Specified") return 1;
            if (b === "No Unit Specified") return -1;
            return a.localeCompare(b);
        });
    }, [filteredPosts]);

    const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSchool = e.target.value;
        setSelectedSchool(newSchool);
    };

    const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCourse(e.target.value);
    };

    return (
        <div>
            <h1>Hello, {user?.displayName}</h1>
            
            {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                    {error}
                </div>
            )}
            
            <select 
                className="dropdownselect" 
                value={selectedSchool} 
                onChange={handleSchoolChange}
                disabled={loading}
            >
                <option value="">School Select</option>
                {schoolOptions.map((school, index) => (
                    <option key={index} value={school}>{school}</option>
                ))}
            </select>
            
            <select 
                className="dropdownselect" 
                value={selectedCourse} 
                onChange={handleCourseChange}
                disabled={loading || !selectedSchool}
            >
                <option value="">Course Select</option>
                {courseOptions.map((course, index) => (
                    <option key={index} value={course}>{course}</option>
                ))}
            </select>
            
            {loading && (
                <div className="loading-indicator">Loading posts...</div>
            )}
            
            <div className="rounded-lg">
                {selectedSchool === '' && selectedCourse === '' ? (
                    <DefaultScreen />
                ) : (
                    <>
                        {/* Unit Filter Dropdown */}
                        {(selectedSchool && selectedCourse && selectedSchool !== "__add_new__" && selectedCourse !== "__add_new__") && (
                            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                                <h3 style={{ color: 'white', marginBottom: '10px' }}>Filter by Unit:</h3>
                                <select 
                                    value={selectedUnitFilter}
                                    onChange={(e) => setSelectedUnitFilter(e.target.value)}
                                    className="dropdownselect"
                                    style={{ marginBottom: '20px' }}
                                >
                                    <option value="">All Units</option>
                                    {availableUnits.map((unit, index) => (
                                        <option key={index} value={unit}>
                                            {unit === "No Unit Specified"}{unit}
                                        </option>
                                    ))}
                                </select>
                                <p style={{ color: '#888', fontSize: '14px', margin: '5px 0 0 0' }}>
                                    {selectedUnitFilter 
                                        ? `Filtered to: ${selectedUnitFilter}`
                                        : `Showing all ${filteredPosts.length} posts grouped by unit`
                                    }
                                </p>
                            </div>
                        )}
                        
                        {Object.keys(groupedPosts).length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>
                                <h3>No posts found for this selection.</h3>
                            </div>
                        ) : (
                            Object.entries(groupedPosts).map(([unit, posts]) => (
                                <div key={unit} style={{ marginBottom: '30px'}}>
                                    <div style={{ 
                                        backgroundColor: 'rgba(0,0,0,0.3)', 
                                        padding: '15px', 
                                        borderRadius: '8px',
                                        border: '1px solid #555',
                                        width:'50%',
                                        margin: '0 auto',
                                        marginBottom: '15px'
                                    }}>
                                        <h2 style={{ 
                                            color: 'white', 
                                            margin: '0',
                                            fontSize: '1.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}>
                                            {unit === "No Unit Specified"} {unit}
                                            <span style={{ 
                                                fontSize: '0.9rem', 
                                                color: '#888',
                                                fontWeight: 'normal'
                                            }}>
                                                ({posts.length} post{posts.length !== 1 ? 's' : ''})
                                            </span>
                                        </h2>
                                    </div>
                                    <div style={{ paddingLeft: '20px' }}>
                                        {posts.map((post) => (
                                            <div key={post.id} style={{ marginBottom: '15px' }}>
                                                <Post 
                                                    post={post} 
                                                    onPostDeleted={handlePostDeleted}
                                                    userRole={userRole}
                                                    currentUser={user}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    );
};